import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Button,
  FormControl, Select, MenuItem,
} from "@mui/material";
import ReactPaginate from "react-paginate";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Spinner from "../../../Component/spinner/spinner";
import {
  useGetAllResourceCategoryPaginationMutation,
  useDeleteResourceCategoryMutation
} from "../../../app/services/resourceCategoryServices";

export default function ListResourceCategory() {
  const [categories, setCategories] = useState([]);
  const navigate = useNavigate();
  const [getAllCategories, { isLoading }] = useGetAllResourceCategoryPaginationMutation();
  const [deleteCategory] = useDeleteResourceCategoryMutation();
  const currentLocation = useLocation();
  var currentPath = currentLocation.pathname.split('/');
  const [page, setPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState("");

  const pageCount = Math.ceil(totalItems / itemsPerPage);

  const fetchCategories = useCallback(async () => {
    try {
      const data = await getAllCategories({ page: page + 1, itemsPerPage }).unwrap();
      if (data.status) {
        setCategories(data.data.resourceCategoryDetails || []);
        setTotalItems(data.data.totalCount || 0);
      } else {
        toast.error(data.err?.message || "Failed to fetch categories");
      }
    } catch (error) {
      toast.error("Error fetching categories");
    }
  }, [page, itemsPerPage, getAllCategories])

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this category?")) {
      try {
        const data = await deleteCategory(id).unwrap();
        if (data.status) {
          toast.success("Category deleted successfully");
          fetchCategories();
        } else {
          toast.error(data.err?.message || "Failed to delete");
        }
      } catch (error) {
        toast.error("Error deleting category");
      }
    }
  };

  const handlePageClick = ({ selected }) => {
    setPage(selected);
  };

  const handlePageChange = () => {
    const pageNumber = parseInt(searchTerm, 10);
    if (isNaN(pageNumber) || pageNumber < 1 || pageNumber > pageCount) {
      setError(`Please enter a valid page number between 1 and ${pageCount}`);
    } else {
      setError("");
      setPage(pageNumber - 1);
    }
  };

  return (
    <Box>
      <div className="pt-5">
        <div className="chapter-heading">
          <h2 className="text-center title-font text-light py-3 mt-1">Resource Categories</h2>
        </div>
      </div>
      <div className='p-md-5 p-3 overallPadding'>
        <div className="text-end w-100">
          <Button variant="contained" className="menus-color poppins-regular" style={{ color: "white", marginBottom: "15px" }} onClick={() => navigate("/" + currentPath[1] + "/add-resource-category")}>
            Add Category
          </Button>
        </div>
        <Box>
          <TableContainer component={Paper} sx={{ boxShadow: 0 }}>
            <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
              <TableHead>
                <TableRow className='menus-color'>
                  <TableCell sx={{ textAlign: "center", color: "#ffff" }}>Name</TableCell>
                  <TableCell sx={{ textAlign: "center", color: "#ffff", width: "50%" }}>Description</TableCell>
                  <TableCell sx={{ textAlign: "center", color: "#ffff" }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center"><Spinner /></TableCell>
                  </TableRow>
                ) : categories?.length > 0 ? (
                  categories.map((row, index) => (
                    <TableRow
                      hover
                      key={row._id}
                      sx={{
                        cursor: "pointer",
                        backgroundColor: index % 2 === 0 ? "#f0f0f0" : "white",
                      }}
                    >
                      <TableCell align="center">{row.name}</TableCell>
                      <TableCell align="center">{row.description}</TableCell>
                      <TableCell align="center">
                        <IconButton color="primary" onClick={() => navigate(`/${currentPath[1]}/edit-resource-category/${row._id}`)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton color="error" onClick={() => handleDelete(row._id)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center">No Categories Found</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <br />
        <br />
        {(categories?.length > 0) && (
          <div className="d-flex justify-content-between flex-lg-row flex-column gx-3 gx-lg-0 content-font" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "20px", marginBottom: "56px" }}>
            <div className="mb-2 mb-md-0">
              <div className="d-flex gap-2">
                <input
                  type="number"
                  min={1}
                  placeholder="Page no."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
                <div className="goPage cursor-pointer" onClick={handlePageChange}>
                  <h5 className="p-2 m-0">Go</h5>
                </div>
              </div>
              {error && <p style={{ color: "red", fontSize: "14px" }}>{error}</p>}
            </div>
            <div className="my-3 my-lg-0">
              <ReactPaginate
                pageCount={pageCount}
                pageRangeDisplayed={2}
                marginPagesDisplayed={1}
                onPageChange={handlePageClick}
                containerClassName={"pagination"}
                activeClassName={"active"}
                forcePage={page}
                previousLabel={pageCount > 1 && page >= 1 ? "Previous" : null}
                nextLabel={pageCount > 1 && page + 1 !== pageCount ? "Next" : null}
                disabledClassName={"disablebtn"}
              />
            </div>
            <div className="mb-2 mb-md-0">
              <FormControl variant="outlined">
                <Select
                  className="itemsperpage"
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(parseInt(e.target.value, 10));
                    setError('');
                    setSearchTerm('');
                    setPage(0);
                  }}
                  MenuProps={{
                    MenuListProps: { sx: { display: 'flex', flexDirection: 'column' } },
                  }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </div>
          </div>
        )}
      </div>
    </Box>
  );
}
